/**
 * 登录页面
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { authApi } from '../services/authApi';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const setAuth = useAuthStore((state) => state.setAuth);
  
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetMode, setResetMode] = useState(false);
  const [resetData, setResetData] = useState({ username: '', email: '', new_password: '', confirm: '' });
  const [resetMsg, setResetMsg] = useState('');

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/home', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.username || !formData.password) {
      setError('请输入用户名和密码');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const response = await authApi.login(formData);
      if (!response.data?.access_token || !response.data?.user) {
        setError('登录响应格式错误，请稍后重试');
        setLoading(false);
        return;
      }
      setAuth(response.data.access_token, response.data.user);
      const user = response.data.user;
      if (user.role === 'admin') {
        navigate('/home', { replace: true });
      } else if (user.subscription_expiry && new Date(user.subscription_expiry) > new Date()) {
        navigate('/home', { replace: true });
      } else {
        navigate('/home', { replace: true });
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || '登录失败，请检查用户名和密码';
      setError(errorMessage);
      setLoading(false);
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResetMsg('');
    if (!resetData.username || !resetData.email || !resetData.new_password) {
      setError('请填写所有字段');
      return;
    }
    if (resetData.new_password.length < 6) {
      setError('新密码至少6位');
      return;
    }
    if (resetData.new_password !== resetData.confirm) {
      setError('两次密码不一致');
      return;
    }
    setLoading(true);
    try {
      await authApi.resetPassword({
        username: resetData.username,
        email: resetData.email,
        new_password: resetData.new_password,
      });
      setResetMsg('密码已重置，请登录');
      setTimeout(() => { setResetMode(false); setResetMsg(''); setResetData({ username: '', email: '', new_password: '', confirm: '' }); }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.detail || '重置失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-background to-emerald-50 px-4">
      <div className="w-full max-w-[400px] bg-white rounded-2xl border border-border shadow-modal p-8">
        {/* Logo + 标题 */}
        <div className="text-center mb-8">
          <img
            src={`${process.env.PUBLIC_URL}/logo.png`}
            alt="小灵标"
            className="mx-auto mb-4 h-20 w-20 rounded-xl object-cover"
          />
          <h1 className="text-2xl font-extrabold text-foreground tracking-tight text-center">小灵标</h1>
          <p className="mt-1 text-sm text-muted-foreground">智能生成标书，高效专业</p>
        </div>
          {resetMode ? (
            <>
              <h2 className="text-lg font-bold text-foreground mb-6">重置密码</h2>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">{error}</div>
              )}
              {resetMsg && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700">{resetMsg}</div>
              )}

              <form onSubmit={handleReset} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">用户名</label>
                  <input type="text" required value={resetData.username}
                    onChange={e => setResetData({ ...resetData, username: e.target.value })}
                    className="input"
                    placeholder="请输入用户名" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">注册邮箱</label>
                  <input type="email" required value={resetData.email}
                    onChange={e => setResetData({ ...resetData, email: e.target.value })}
                    className="input"
                    placeholder="请输入注册时使用的邮箱" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">新密码</label>
                  <input type="password" required value={resetData.new_password}
                    onChange={e => setResetData({ ...resetData, new_password: e.target.value })}
                    className="input"
                    placeholder="至少6位" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">确认密码</label>
                  <input type="password" required value={resetData.confirm}
                    onChange={e => setResetData({ ...resetData, confirm: e.target.value })}
                    className="input"
                    placeholder="再次输入新密码" />
                </div>
                <button type="submit" disabled={loading}
                  className="btn-primary w-full">
                  {loading ? '重置中...' : '重置密码'}
                </button>
              </form>

              <p className="mt-4 text-center text-xs text-muted-foreground">
                <button onClick={() => { setResetMode(false); setError(''); setResetMsg(''); }}
                  className="font-semibold text-primary hover:text-primary/80">
                  ← 返回登录
                </button>
              </p>
            </>
          ) : (
            <>
              <h2 className="text-lg font-bold text-foreground mb-6">登录</h2>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="username" className="block text-xs font-medium text-muted-foreground mb-1.5">
                用户名或邮箱
              </label>
              <input
                id="username"
                type="text"
                required
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className="input"
                placeholder="请输入用户名或邮箱"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-xs font-medium text-muted-foreground mb-1.5">
                密码
              </label>
              <input
                id="password"
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="input"
                placeholder="请输入密码"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
            >
              {loading ? '登录中...' : '登录'}
            </button>
          </form>

          <p className="mt-6 text-center text-xs text-muted-foreground">
            还没有账户？{' '}
            <Link to="/register" className="font-semibold text-primary hover:text-primary/80">
              立即注册
            </Link>
          </p>
          <p className="mt-2 text-center text-xs text-muted-foreground">
            <button onClick={() => { setError(''); setResetMsg(''); setResetMode(true); }}
              className="text-muted-foreground hover:text-primary">
              忘记密码？
            </button>
          </p>
          </>
        )}
        </div>
    </div>
  );
};

export default Login;
